import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '@environments/environment';
import {
  GetLoginSettingsResponse,
  IpWhitelistDetailResponse,
  IpWhitelistListResponse,
  UpdateLoginSettingsPayload,
  UpdateLoginSettingsResponse,
  CreateIpRestrictionPayload,
  CreateIpRestrictionResponse,
  UpdateIpRestrictionStatusPayload,
  UpdateIpRestrictionStatusResponse,
  UserListResponse,
} from '../models/user-access-management.model';
import { ApiResponse } from '@shared/models/api-response.models';

/**
 * All HTTP calls in this service return the standard
 * `ApiResponse<T>` envelope. Callers read inner payloads via `res.data`.
 */
@Injectable({ providedIn: 'root' })
export class UserAccessManagementService {
  private readonly apiUrl = environment.userManagementApiUrl;

  constructor(private readonly http: HttpClient) {}

  getLoginSettings(): Observable<ApiResponse<GetLoginSettingsResponse>> {
    return this.http.get<ApiResponse<GetLoginSettingsResponse>>(
      `${this.apiUrl}/api/account/v1/settings/login`
    );
  }

  updateLoginSettings(
    payload: UpdateLoginSettingsPayload
  ): Observable<ApiResponse<UpdateLoginSettingsResponse>> {
    return this.http.post<ApiResponse<UpdateLoginSettingsResponse>>(
      `${this.apiUrl}/api/account/v1/settings/login`,
      payload
    );
  }

  getIpWhitelistList(
    page: number = 1,
    limit: number = 10
  ): Observable<IpWhitelistListResponse> {
    return this.http.get<IpWhitelistListResponse>(
      `${this.apiUrl}/api/ip-whitelist/v1`,
      { params: { page: page.toString(), limit: limit.toString() } }
    );
  }

  getIpWhitelistDetail(userId: string): Observable<IpWhitelistDetailResponse> {
    return this.http.get<IpWhitelistDetailResponse>(
      `${this.apiUrl}/api/ip-whitelist/v1/${userId}`
    );
  }

  /**
   * `userId === 'system'` replaces the platform-wide whitelist; any other value
   * is one user's. The system write is reached only by naming it (F-183).
   */
  createIpRestriction(
    userId: string,
    payload: CreateIpRestrictionPayload
  ): Observable<ApiResponse<CreateIpRestrictionResponse>> {
    const endpoint =
      userId !== 'system'
        ? `${this.apiUrl}/api/ip-whitelist/v1/${userId}`
        : `${this.apiUrl}/api/ip-whitelist/v1/system`;
    return this.http.put<ApiResponse<CreateIpRestrictionResponse>>(
      endpoint,
      payload
    );
  }

  deleteIpRestriction(userId: string): Observable<ApiResponse<unknown>> {
    const endpoint =
      userId !== 'system'
        ? `${this.apiUrl}/api/ip-whitelist/v1/${userId}`
        : `${this.apiUrl}/api/ip-whitelist/v1/system`;
    return this.http.delete<ApiResponse<unknown>>(endpoint);
  }

  updateIpRestrictionStatus(
    userId: string,
    payload: UpdateIpRestrictionStatusPayload
  ): Observable<ApiResponse<UpdateIpRestrictionStatusResponse>> {
    const endpoint =
      userId !== 'system'
        ? `${this.apiUrl}/api/ip-whitelist/v1/${userId}/status`
        : `${this.apiUrl}/api/ip-whitelist/v1/system/status`;
    return this.http.put<ApiResponse<UpdateIpRestrictionStatusResponse>>(
      endpoint,
      payload
    );
  }

  /** `status` omitted = users of every status. user-management caps `limit` at 20. */
  getUserList(
    status?: string,
    page: number = 1,
    limit: number = 20
  ): Observable<UserListResponse> {
    const params: Record<string, string> = {
      page: page.toString(),
      limit: limit.toString(),
    };
    if (status) {
      params['status'] = status;
    }
    return this.http.get<UserListResponse>(`${this.apiUrl}/api/users`, {
      params,
    });
  }
}
